import React from 'react';
import { Button, Card, Icon } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import clsx from 'clsx';
import { InformationMessage } from 'components/InformationMessage/InformationMessage';
import { InnerLayout } from 'components/Layout/InnerLayout/InnerLayout';
import { LayoutHeadings } from 'components/Layout/InnerLayout/LayoutHeadings/LayoutHeadings';
import { SideNavigation } from 'components/Navigation/SideNavigation';
import { useOffers } from 'components/Signup/hooks/useOffers';
import { useAmplitude } from 'hooks/useAmplitude';
import { useGetIntroducerApplication } from 'lendingApi/lendingApiHooks';
import { REQUESTED_AMOUNT_CONTACT_THRESHOLD } from 'utils/applications/signup.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { DocumentGroup } from './DocumentGroup/DocumentGroup';
import { renderSubheading } from './Documents.helper';
import styles from './Documents.module.css';
import { DocumentsLoader } from './DocumentsLoader';
import { OtherDocumentGroup } from './OtherDocumentGroup/OtherDocumentGroup';

export const Documents = () => {
  const { trackEvent } = useAmplitude();

  const { application, isLoading: isApplicationLoading } =
    useGetIntroducerApplication();

  const isPartnerChannel = application?.is_partner_channel || false;

  const aboveContactThreshold =
    Number(application?.requested_products[0].amount) >=
    REQUESTED_AMOUNT_CONTACT_THRESHOLD;

  const {
    documentRequirements,
    isLoadingOffers,
    isRefetchingOffers,
    refetchOffersData,
  } = useOffers({
    getOffersEnabled: true,
  });

  const isloading = !application || isApplicationLoading || isLoadingOffers;
  const hasNodocumentRequirements = !isloading && !documentRequirements;

  const handleRefreshRequirements = () => {
    trackEvent(AMPLITUDE_EVENTS.refreshRequirementsClicked, {
      'CTA text': 'Refresh requirements',
    });

    refetchOffersData();
  };

  return (
    <>
      <SideNavigation />
      {isloading ? (
        <InnerLayout hasSidebar maxWidth="740px">
          <DocumentsLoader />
        </InnerLayout>
      ) : (
        <InnerLayout
          headingComponent={
            <LayoutHeadings
              heading={t`Documents`}
              headingTestId="document-title"
              subHeading={renderSubheading(
                documentRequirements,
                isPartnerChannel,
              )}
              className={clsx({
                [styles.hasInfoMessage]: aboveContactThreshold,
              })}
              showManagedByIwocaPill={isPartnerChannel}
            />
          }
          hasSidebar
          maxWidth="740px"
        >
          {aboveContactThreshold && (
            <div className={styles.infoMessageContainer}>
              <InformationMessage message="For request amounts of £350,000 and above, our underwriting team may contact the applicant directly. We’ll notify you in advance." />
            </div>
          )}
          {documentRequirements
            ?.filter((requirement) => {
              return !requirement.is_optional;
            })
            ?.map((requirement) => {
              return (
                <DocumentGroup
                  isLoading={isRefetchingOffers}
                  isPartnerChannel={isPartnerChannel}
                  data-testid="required-document-group"
                  key={requirement.type}
                  requirement={requirement}
                />
              );
            })}
          {documentRequirements
            ?.filter((requirement) => {
              return requirement.is_optional;
            })
            ?.map((requirement) => {
              return (
                <DocumentGroup
                  isLoading={isRefetchingOffers}
                  isPartnerChannel={isPartnerChannel}
                  data-testid="optional-document-group"
                  key={requirement.type}
                  requirement={requirement}
                />
              );
            })}
          {documentRequirements && documentRequirements?.length > 0 && (
            <>
              <OtherDocumentGroup
                isPartnerChannel={isPartnerChannel}
                data-testid="other-document-group"
                key="other"
              />
              {!isPartnerChannel && (
                <div className={styles.buttonContainer}>
                  <Button
                    className={styles.button}
                    loading={isRefetchingOffers}
                    onClick={handleRefreshRequirements}
                    size="small"
                    variant="denim"
                  >
                    <Trans>Refresh requirements</Trans>
                    <Icon icon="refreshCcw" />
                  </Button>
                </div>
              )}
            </>
          )}
          {hasNodocumentRequirements && (
            <Card className={styles.noActionCard}>
              <Icon icon="searchCheck" />
              <p>
                <Trans>We're checking your information...</Trans>
              </p>
            </Card>
          )}
          {isPartnerChannel && (
            <div className={styles.managedByIwocaFootnote}>
              <Trans>
                *Only the applicant can take actions on apps managed by iwoca.
              </Trans>
            </div>
          )}
        </InnerLayout>
      )}
    </>
  );
};
